import axios from 'axios';
import { call, all, put, takeLatest, select, delay } from 'redux-saga/effects';

import history from '~/services/history';
import notification from '~/services/notification';
import productService from '../product/product-service';
import productPhotosService from './product-photos-service';
import {
  EDIT_ITEM,
  DELETE_ITEMS,
  GET_ALL_PRODUCTS_URLS_REQUESTS,
  GET_ALL_REQUEST,
  GET_BY_ID_REQUEST,
  GET_FILTER_PRODUCT_REQUEST,
  SAVE_REQUEST,
  GET_FILTER_SELECTS_REQUEST
} from './product-photos-constants';
import * as productPhotosActions from './product-photos-actions';

import { ISA_ERROR } from '~/constants';

import { AppStateType } from '../root-reducer';

import { formatMessage } from '~/i18n/translate';
import domainService from '~/services/modules/domain/domain-service';

let cancel: any;
const CancelToken = axios.CancelToken;

const INITIAL_VALUES = {
  products: [],
  pictureUrl: ''
};

export function* getAll({ payload }: any) {
  if (cancel !== undefined) cancel();
  const cancelToken = new CancelToken(function executor(c) {
    cancel = c;
  });

  try {
    yield put(productPhotosActions.setLoadingData(true));

    const { rowsPerPage, page, order, orderBy } = yield select((state: AppStateType) => state.productPhotos.list);

    const pagination = {
      skip: page === 0 ? 0 : page * rowsPerPage,
      limit: rowsPerPage,
      order,
      orderBy
    };

    const filters = {
      sectionsExternalCodes: payload?.sectionsExternalCodes?.map(section => section.externalCode)?.join(',') ?? '',
      departmentExternalCodes:
        payload?.departmentExternalCodes?.map(department => department.externalCode)?.join(',') ?? '',
      providersExternalCodes:
        payload?.providersExternalCodes?.map(providers => providers.externalCode)?.join(',') ?? '',
      brandsExternalCodes: payload?.brandsExternalCodes?.map(brand => brand.externalCode)?.join(',') ?? '',
      description: payload?.description?.toLowerCase() ?? '',
      productsLinesExternalCodes:
        payload?.productLinesExternalCodes?.map(productLine => productLine.externalCode)?.join(',') ?? '',
      categoriesExternalCodes: payload?.categoryExternalCodes?.map(category => category.externalCode)?.join(',') ?? ''
    };

    const [{ data: products }, { data: productsCount }] = yield all([
      call(
        productPhotosService.getListPicturesProducts,
        {
          ...pagination,
          ...filters
        },
        {
          cancelToken
        }
      ),
      call(
        productPhotosService.getListPicturesProductsCount,
        {
          ...filters
        },
        {
          cancelToken
        }
      )
    ]);

    yield put(
      productPhotosActions.getAllSuccess({
        products,
        rowsCount: productsCount?.count
      })
    );
  } catch (error) {
    yield put({ type: ISA_ERROR, error });
  } finally {
    yield put(productPhotosActions.setLoadingData(false));
  }
}

export function* getById({ id }: any) {
  yield put(productPhotosActions.setLoadingData(true));

  try {
    if (cancel !== undefined) cancel();
    const cancelToken = new CancelToken(function executor(c) {
      cancel = c;
    });

    const { data: products } = yield call(productService.getRawProducts, {
      skip: 0,
      limit: 50
    });

    if (id) {
      const { data: picture } = yield call(productPhotosService.getPictureById, id, {
        cancelToken
      });

      const parsed = {
        pictureUrl: picture?.pictures[0],
        products: products?.filter(product => product?.externalCode === id)
      };

      yield put(
        productPhotosActions.getByIdSuccess({
          data: parsed,
          selects: {
            products
          }
        })
      );
    } else {
      yield put(
        productPhotosActions.getByIdSuccess({
          data: INITIAL_VALUES,
          selects: {
            products
          }
        })
      );
    }
  } catch (error) {
  } finally {
    yield put(productPhotosActions.setLoadingData(false));
  }
}

export function* queryProducts({ queryText = '', skip }: any) {
  yield delay(200);

  if (cancel !== undefined) cancel();

  const cancelToken = new CancelToken(function executor(c) {
    cancel = c;
  });

  const { selects } = yield select((state: AppStateType) => state.productPhotos.form);

  try {
    yield put(productPhotosActions.setLoadingSelects(true));
    const { data: products } = yield call(
      productService.getRawProducts,
      {
        skip,
        limit: 50,
        queryString: queryText
      },
      {
        cancelToken
      }
    );

    const existingProductIds = selects?.products.map(product => product?.externalCode);
    const filteredProducts = products?.filter(product => !existingProductIds?.includes(product?.externalCode));
    const parsedProducts =
      [...selects?.products, ...filteredProducts]?.sort((a, b) => a?.name.localeCompare(b?.name)) ?? [];

    yield put(productPhotosActions.getFilterProductSuccess(parsedProducts));
  } catch (error) {
    yield put({ type: ISA_ERROR, error });
  } finally {
    yield put(productPhotosActions.setLoadingSelects(false));
  }
}

export function* savePhotosRequest({ payload, id }: any) {
  try {
    yield put(productPhotosActions.setLoadingSend(true));

    const parsedProducts = payload?.product?.externalCode
      ? [payload?.product?.externalCode]
      : payload?.data?.products?.map((product: any) => product?.externalCode) ?? [];

    const { data: files } = yield call(productPhotosService.uploadPicture, payload?.files);

    if (files?.length > 0) {
      yield call(productPhotosService.savePictures, {
        productsExternalCodes: parsedProducts,
        pictureUrl: files[0]?.pictureUrl
      });
    }

    if (payload?.product?.externalCode) {
      yield put(productPhotosActions.getAllRequest());
    } else {
      yield call(history.push, '/register/product-photos');
    }

    yield call(
      notification.success,
      id ? formatMessage('productsPhotosFormEditSuccessMessage') : formatMessage('productsPhotosFormSaveSuccessMessage')
    );
  } catch (error) {
    yield put({ type: ISA_ERROR, error });
  } finally {
    yield put(productPhotosActions.setLoadingSend(false));
  }
}

export function* editItem({ payload }: any) {
  try {
    if (payload?.files?.length === 0 || payload?.selectedRows?.length === 0) {
      return yield call(notification.warning, formatMessage('productsPhotosFormEmptyPhotos'));
    }

    yield put(productPhotosActions.setLoadingSend(true));

    const { data: files } = yield call(productPhotosService.uploadPicture, payload?.files);

    const parsedProducts = payload?.selectedRows?.map((product: any) => product?.externalCode) ?? [];

    if (files?.length > 0) {
      yield call(productPhotosService.savePictures, {
        productsExternalCodes: parsedProducts,
        pictureUrl: files[0]?.pictureUrl
      });
    }

    yield put(productPhotosActions.getAllRequest());
    yield call(notification.success, formatMessage('productsPhotosFormEditSuccessMessage'));
  } catch (error) {
    yield put({ type: ISA_ERROR, error });
  } finally {
    yield put(productPhotosActions.setLoadingSend(false));
  }
}

export function* deleteItems({ payload }: any) {
  try {
    const { isConfirmed } = yield call(
      notification.confirm,
      formatMessage('commonLabelExclude'),
      formatMessage('productsPhotosFormDescriptionDelete')
    );

    if (isConfirmed) {
      yield put(productPhotosActions.setLoadingSend(true));

      const pictures = [payload[0]?.pictures[0]];
      const parsedProducts = payload?.map(product => product?.externalCode)?.join(',') ?? [];

      yield call(productPhotosService.deletePictures, {
        pictures,
        productsExternalCodes: parsedProducts
      });

      yield put(productPhotosActions.getAllRequest());
      yield call(notification.success, formatMessage('productsPhotosFormDeleteSuccessMessage'));
    }
  } catch (error) {
    yield put({ type: ISA_ERROR, error });
  } finally {
    yield put(productPhotosActions.setLoadingSend(false));
  }
}

export function* getAllProductsUrls({ pictureUrl }: any) {
  try {
    yield put(productPhotosActions.setLoadingModal(true));

    const { data: picturesUrls } = yield call(productPhotosService.getPicturesUrls, pictureUrl);

    yield put(productPhotosActions.getAllProductUrlSuccess(picturesUrls));
  } catch (error) {
    yield put({ type: ISA_ERROR, error });
  } finally {
    yield put(productPhotosActions.setLoadingModal(false));
  }
}

export function* getAllFilterSelects({ payload }: any) {
  try {
    yield put(productPhotosActions.setLoadingSelects(true));

    const [
      { data: brand },
      { data: category },
      { data: department },
      { data: provider },
      { data: section },
      { data: productLines }
    ] = yield all([
      call(domainService.getBrands),
      call(domainService.getCategories),
      call(domainService.getDepartments),
      call(domainService.getProviders),
      call(domainService.getSections),
      call(domainService.getProductLines)
    ]);

    const selects = {
      brand,
      category,
      department,
      provider,
      section,
      productLines
    };

    yield put(productPhotosActions.getAllSelectsSuccess(selects));
  } catch (error) {
    yield put({ type: ISA_ERROR, error });
  } finally {
    yield put(productPhotosActions.setLoadingSelects(false));
  }
}

export default all([
  takeLatest(GET_ALL_REQUEST, getAll),
  takeLatest(GET_BY_ID_REQUEST, getById),
  takeLatest(GET_FILTER_PRODUCT_REQUEST, queryProducts),
  takeLatest(SAVE_REQUEST, savePhotosRequest),
  takeLatest(EDIT_ITEM, editItem),
  takeLatest(DELETE_ITEMS, deleteItems),
  takeLatest(GET_ALL_PRODUCTS_URLS_REQUESTS, getAllProductsUrls),
  takeLatest(GET_FILTER_SELECTS_REQUEST, getAllFilterSelects)
]);
